import { FieldType } from '@getaccept/lib-shared/src/fields/types/enums/field-type';
import type { Field } from '@getaccept/lib-shared/src/fields/types/field';
import { MergeTagHelper } from '@getaccept/lib-shared/src/helpers/merge-tag.helper';
import { defineStore } from 'pinia';
import type { AlignFieldType } from '@getaccept/lib-shared/src/fields/types/enums/align-field-type';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { FieldPosition } from '@getaccept/lib-shared/src/fields/types/field-position';
import { FieldPlacementHelper } from '@getaccept/lib-shared/src/fields/helpers/field-placement.helper';
import { FieldHelper } from '@getaccept/lib-shared/src/fields/helpers/field.helper';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useToast } from '@getaccept/lib-shared/src/toast';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { FieldSource } from '@getaccept/lib-shared/src/fields/types/enums/field-source';
import { FieldService } from '../../../../api/fields/services/field.service';

export const useFieldsStore = defineStore('fields', () => {
  const loading = ref(false);
  const documentId = ref(null);
  const fields: Ref<Field[]> = ref([]);

  const load = async (_documentId: string) => {
    documentId.value = _documentId;
    loading.value = true;
    try {
      const fieldsResponse: Field[] = await FieldService.load(_documentId);
      const mappedFields: Field[] = fieldsResponse.map((field: Field) =>
        field.type === FieldType.Merge
          ? { ...field, displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value) }
          : field
      );
      fields.value = mappedFields;
    } catch (err) {
      console.error(err);
    } finally {
      loading.value = false;
    }
  };

  const add = (_fields: Field[]) => {
    fields.value = [...fields.value, ..._fields];
  };

  const remove = (_fields: Field[]) => {
    fields.value = fields.value.filter(
      (stateField: Field) => !_fields.map((field: Field) => field.id).includes(stateField.id)
    );
  };

  const replaceCreatedFields = (fieldsWithId: { tempId: string; field: Field }[]) => {
    const fieldsWithCorrectId: Field[] = fields.value.map(
      (field: Field) =>
        fieldsWithId.find(
          (fieldWithId: { tempId: string; field: Field }) =>
            fieldWithId.tempId === field.id && fieldWithId.field.pageId === field.pageId
        )?.field || field
    );
    fields.value = fieldsWithCorrectId;
  };

  const lastUpdatedFieldId: Ref<string> = ref(null);
  const setLastUpdatedFieldId = (updatedFields: Field[]) => {
    const sortedUpdatedFields: Field[] = FieldHelper.sortByPositionWithoutPages(updatedFields);
    lastUpdatedFieldId.value = sortedUpdatedFields[sortedUpdatedFields.length - 1]?.id;
  };

  const update = async (updatedFields: Field[]) => {
    if (!updatedFields.length) {
      return;
    }

    const previousFields = [...fields.value];

    const updatedFieldsMap = new Map(updatedFields.map(field => [field.id, field]));

    fields.value = previousFields.map(stateField => {
      const update = updatedFieldsMap.get(stateField.id);
      if (update && !!Object.keys(update).length) {
        return {
          ...stateField,
          ...update,
          recipientId: update.recipientId ? update.recipientId : stateField.recipientId || null,
        };
      }
      return stateField;
    });

    setLastUpdatedFieldId(updatedFields);
    try {
      await FieldService.updateMultiple(updatedFields);
    } catch {
      fields.value = previousFields;
      useToast().toast.danger(t('could-not-update-field'));
    }
  };

  const tabStartField: Ref<Field> = ref(null);

  const setTabStart = (_field: Field) => {
    tabStartField.value = _field;
  };

  const tabbedField: Ref<Field> = ref(null);

  const tab = (pages: DocumentPage[]) => {
    const _field: Field = FieldHelper.getNextTabField(fields.value, pages, tabStartField.value);
    tabbedField.value = _field;
  };

  const pageWidth: Ref<number> = ref(0);

  const updatePageWidth = (_pageWidth: number) => {
    pageWidth.value = _pageWidth;
  };

  const align = ({
    actionType,
    fields: _fields,
  }: {
    actionType: AlignFieldType;
    fields: Field[];
  }) => {
    const fieldContainerPosition: FieldPosition =
      FieldPlacementHelper.calculateFieldContainerPosition(_fields);
    const updatedFields: Field[] = FieldHelper.getAlignedFields(
      _fields,
      actionType,
      fieldContainerPosition
    );
    update(updatedFields);
  };

  const move = ({ pageId, fields: _fields }: { pageId: string; fields: Field[] }) => {
    const movedFields: Field[] = _fields.map((field: Field) => ({
      ...field,
      pageId,
      position: FieldPlacementHelper.preventFieldPositionBeingOutsidePage(
        field.position,
        pageId,
        pageWidth.value
      ),
    }));
    update(movedFields);
  };

  const changeTextSize = ({ size, fields: _fields }: { size: number; fields: Field[] }) => {
    const updatedFields: Field[] = _fields.map((selectedField: Field) => {
      if (selectedField.position.height !== size && selectedField.type !== FieldType.Check) {
        selectedField.position.height = size;
      }
      return selectedField;
    });
    update(updatedFields);
  };

  const focusedFieldId: Ref<string> = ref(null);
  const focus = (_fieldId: string) => {
    focusedFieldId.value = _fieldId;
  };

  const senderSignature: Ref<string> = ref(null);
  const loadSenderSignature = async (userId: string) => {
    try {
      const signatureData: string = await FieldService.loadUserSignature(userId);
      senderSignature.value = signatureData;
    } catch (e) {
      useToast().toast.danger(t('could-not-load-signature'));
    }
  };

  const isDragging: Ref<boolean> = ref(false);
  const dragField = (_isDragging: boolean) => {
    isDragging.value = _isDragging;
  };

  const fieldsToFillOut: ComputedRef<Field[]> = computed(() => {
    if (!fields.value.length) {
      return [];
    }
    return fields.value.filter(
      (field: Field) =>
        ([FieldType.Dropdown, FieldType.Formatted, FieldType.Check].includes(field.type) &&
          field.source === FieldSource.Sender) ||
        field.type === FieldType.Text
    );
  });

  const lastUpdatedField: ComputedRef<Field> = computed(() => {
    if (!fields.value?.length || !lastUpdatedFieldId.value) {
      return null;
    }
    return fields.value.find((field: Field) => field.id === lastUpdatedFieldId.value);
  });

  return {
    loading,
    documentId,
    fields,
    lastUpdatedFieldId,
    tabStartField,
    tabbedField,
    pageWidth,
    focusedFieldId,
    senderSignature,
    isDragging,
    fieldsToFillOut,
    lastUpdatedField,

    load,
    add,
    remove,
    replaceCreatedFields,
    setLastUpdatedFieldId,
    update,
    setTabStart,
    tab,
    updatePageWidth,
    align,
    move,
    changeTextSize,
    focus,
    loadSenderSignature,
    dragField,
  };
});
